import { useState, type ReactNode } from "react";
import type { ShapeKind } from "@coaching/types";
import { ANNOTATION_COLOURS } from "@coaching/annotations";
import { Icon } from "./icons";

export interface ToolState {
    kind: ShapeKind;
    color: string;
    width: number;
}

export const STROKES: { label: string; width: number }[] = [
    { label: "Fine", width: 0.0025 },
    { label: "Medium", width: 0.004 },
    { label: "Bold", width: 0.007 },
];

const TOOLS: { kind: ShapeKind; label: string; key: string; icon: () => JSX.Element }[] = [
    { kind: "path", label: "Pen", key: "P", icon: Icon.pen },
    { kind: "line", label: "Line", key: "L", icon: Icon.line },
    { kind: "arrow", label: "Arrow", key: "A", icon: Icon.arrow },
    { kind: "circle", label: "Circle", key: "C", icon: Icon.circle },
    { kind: "rect", label: "Box", key: "B", icon: Icon.box },
];

const RECENT_KEY = "swana-coach.custom-colours";
const RECENT_MAX = 3;
const readRecent = (): string[] => {
    try {
        const v: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
        return Array.isArray(v) ? v.filter((c): c is string => typeof c === "string" && /^#[0-9a-f]{6}$/i.test(c)).slice(0, RECENT_MAX) : [];
    } catch {
        return [];
    }
};
const writeRecent = (list: string[]) => {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* private window: the picks last this visit only */ }
};

/** Tools, colours (the five presets, the coach's own recent picks and a picker), stroke and history. */
export function Toolbar({ tool, onChange, onUndo, onRedo, onClear, canUndo, canRedo, canClear, disabled, voice }: {
    tool: ToolState;
    onChange: (t: ToolState) => void;
    onUndo: () => void;
    onRedo: () => void;
    onClear: () => void;
    canUndo: boolean;
    canRedo: boolean;
    canClear: boolean;
    disabled: boolean;
    /** The voice recorder for the moment on screen; never greyed out, so a recording can always be stopped. */
    voice?: ReactNode;
}) {
    const [recent, setRecent] = useState<string[]>(readRecent);
    const presets: readonly string[] = ANNOTATION_COLOURS;
    const custom = !presets.includes(tool.color);

    // The picker reports every colour it passes through while dragging; only the one it closes on is remembered.
    const remember = () => {
        const c = tool.color.toLowerCase();
        if (presets.includes(c) || recent[0] === c) return;
        const next = [c, ...recent.filter((r) => r !== c)].slice(0, RECENT_MAX);
        setRecent(next);
        writeRecent(next);
    };

    return (
        <div className={`toolbar${disabled ? " disabled" : ""}`} role="toolbar" aria-label="Drawing tools">
            <div className="tool-group segmented">
                {TOOLS.map((t) => (
                    <button key={t.kind} type="button" className={`tool${tool.kind === t.kind ? " on" : ""}`} title={`${t.label} (${t.key})`}
                        aria-label={t.label} aria-pressed={tool.kind === t.kind} disabled={disabled} onClick={() => onChange({ ...tool, kind: t.kind })}>
                        <t.icon />
                    </button>
                ))}
            </div>

            <div className="tool-group colours" aria-label="Colour">
                {presets.map((c, i) => (
                    <button key={c} type="button" className={`swatch${tool.color === c ? " on" : ""}`} style={{ "--swatch": c } as React.CSSProperties}
                        title={`Colour ${i + 1} (${i + 1})`} aria-label={`Colour ${i + 1}`} aria-pressed={tool.color === c} disabled={disabled}
                        onClick={() => onChange({ ...tool, color: c })} />
                ))}
                {recent.length > 0 && <span className="tool-divider" aria-hidden />}
                {recent.map((c) => (
                    <button key={c} type="button" className={`swatch${tool.color === c ? " on" : ""}`} style={{ "--swatch": c } as React.CSSProperties}
                        title={`Your colour ${c}`} aria-label={`Your colour ${c}`} aria-pressed={tool.color === c} disabled={disabled}
                        onClick={() => onChange({ ...tool, color: c })} />
                ))}
                <label className={`swatch picker${custom ? " on" : ""}${disabled ? " off" : ""}`} title="Choose any colour"
                    style={custom ? ({ "--swatch": tool.color } as React.CSSProperties) : undefined}>
                    <span className="picker-icon"><Icon.palette /></span>
                    <input type="color" value={custom ? tool.color : "#ff00aa"} disabled={disabled} aria-label="Choose any colour"
                        onChange={(e) => onChange({ ...tool, color: e.target.value.toLowerCase() })} onBlur={remember} />
                </label>
            </div>

            <div className="tool-group segmented" aria-label="Line weight">
                {STROKES.map((s) => (
                    <button key={s.label} type="button" className={`tool${tool.width === s.width ? " on" : ""}`} title={`${s.label} line`}
                        aria-label={`${s.label} line`} aria-pressed={tool.width === s.width} disabled={disabled} onClick={() => onChange({ ...tool, width: s.width })}>
                        <span className="stroke-sample" style={{ height: Math.max(2, Math.round(s.width * 900)), background: tool.color }} aria-hidden />
                    </button>
                ))}
            </div>

            <div className="tool-group history">
                <button type="button" className="tool" title="Undo (Ctrl+Z)" aria-label="Undo" disabled={disabled || !canUndo} onClick={onUndo}><Icon.undo /></button>
                <button type="button" className="tool" title="Redo (Ctrl+Shift+Z)" aria-label="Redo" disabled={disabled || !canRedo} onClick={onRedo}><Icon.redo /></button>
                <button type="button" className="tool" title="Clear this moment's drawing" aria-label="Clear drawing" disabled={disabled || !canClear} onClick={onClear}><Icon.clear /></button>
            </div>

            {voice && (
                <div className="voice-slot">
                    <span className="voice-slot-label">Voice for this moment</span>
                    {voice}
                </div>
            )}
        </div>
    );
}
